/**
 * Browser capability probes for Nova's two voice features (FLIP-277).
 *
 * Voice chat and dictation are different features with different requirements, and before this
 * they shared one gate. Dictation is the Web Speech API and needs `SpeechRecognition`. Voice chat,
 * once it runs over the Realtime API, needs WebRTC and an audio graph and does not touch Web
 * Speech at all - so gating it on `SpeechRecognition` turned away Safari and Firefox, which can
 * run it perfectly well. Widening voice chat is the migration's best side effect, and it is
 * entirely thrown away if this gate is not rewritten.
 *
 * The probes take the object they read rather than reaching for `window`. Karma runs in a real
 * Chrome that has every API here, so a probe reading the global directly could only ever be tested
 * for the case where everything is present - which is the case that never breaks.
 */

/** The APIs a voice feature can be missing, named as the user-facing copy needs to name them. */
export type VoiceCapability = "webrtc" | "microphone" | "audio" | "speech-recognition";

export interface VoiceSupport {
  supported: boolean;
  /** Empty when supported. Order is stable so the copy reads the same way every time. */
  missing: VoiceCapability[];
}

/**
 * The shape of `window` these probes actually read. Deliberately partial and all-optional: the
 * whole point is to describe a browser that is missing some of it.
 */
export interface VoiceCapabilityWindow {
  RTCPeerConnection?: unknown;
  AudioContext?: unknown;
  webkitAudioContext?: unknown;
  SpeechRecognition?: unknown;
  webkitSpeechRecognition?: unknown;
  navigator?: { mediaDevices?: { getUserMedia?: unknown } };
}

/**
 * What voice chat needs on the Realtime path: a peer connection to carry the audio, a microphone
 * to put into it, and an audio graph to play the remote track and drive the meter from.
 *
 * `webkitAudioContext` counts, because `ensureVoiceAudioContext` accepts it. A gate that admitted
 * only the unprefixed name would reject browsers the audio graph itself is happy with - a subtler
 * version of exactly the bug this replaces.
 */
export function probeRealtimeVoiceSupport(win: VoiceCapabilityWindow): VoiceSupport {
  const missing: VoiceCapability[] = [];
  if (!win.RTCPeerConnection) missing.push("webrtc");
  if (!win.navigator?.mediaDevices?.getUserMedia) missing.push("microphone");
  if (!win.AudioContext && !win.webkitAudioContext) missing.push("audio");
  return { supported: missing.length === 0, missing };
}

/**
 * What voice chat needs on the legacy client-acoustic path, which is still what ships until
 * FLIP-278 routes by the flag: the recogniser, exactly as before.
 *
 * This is not dictation's gate - it is voice chat's, on the old path, and it happens to ask the
 * same question. They are separate calls now so that retiring the legacy path (FLIP-279) deletes
 * this one and leaves dictation's alone.
 */
export function probeLegacyVoiceSupport(win: VoiceCapabilityWindow): VoiceSupport {
  const hasRecognition = Boolean(win.SpeechRecognition || win.webkitSpeechRecognition);
  return { supported: hasRecognition, missing: hasRecognition ? [] : ["speech-recognition"] };
}

/** Dictation's own gate. Unchanged in substance, and independent of voice chat's by design. */
export function probeDictationSupport(win: VoiceCapabilityWindow): VoiceSupport {
  return probeLegacyVoiceSupport(win);
}

const CAPABILITY_LABELS: Record<VoiceCapability, string> = {
  webrtc: "live audio calls",
  microphone: "microphone access",
  audio: "audio playback",
  "speech-recognition": "speech recognition",
};

/**
 * Failure copy for the voice-chat gate.
 *
 * Names what is actually absent rather than naming a browser. "Try Chrome" was wrong on both
 * counts: it prescribed a remedy that is not the only one, and on this path it described the
 * wrong feature entirely - it said dictation was unsupported to someone who had pressed voice
 * chat. The last sentence matters as much as the first: there is always still a way to talk to
 * Nova, and the alert should say so rather than reading as a dead end.
 */
export function describeMissingVoiceSupport(missing: VoiceCapability[]): string {
  if (missing.length === 0) return "";
  const labels = missing.map((capability) => CAPABILITY_LABELS[capability]);
  const listed =
    labels.length === 1
      ? labels[0]
      : labels.slice(0, -1).join(", ") + " or " + labels[labels.length - 1];
  return (
    `Voice chat needs ${listed}, which this browser doesn't support. ` +
    "You can still type to Nova."
  );
}
